/* global LANG, $ */

import toArray from '../helpers/toArray';
import { insertAfter, getClosest } from '../helpers/DOMManipulation';

const ManagedForm = {

  /** @type {Array} */
  availableValidityChecks: [
    'badInput',
    'customError',
    'patternMismatch',
    'rangeOverflow',
    'rangeUnderflow',
    'stepMismatch',
    'tooLong',
    'tooShort',
    'typeMismatch',
    'valueMissing',
  ],

  /** @type {Array} */
  inputElementTags: [
    'input',
    'select',
    'textarea',
  ],

  /**
   * Create the managed form component
   * @param {HTMLFormElement} node Form element
   * @param {Object} props Data attributes set on the form element
   * @return {void}
   */
  create(node/* , props */) {
    // Extend the default options with any given inline options with data attributes
    // const options = Object.assign({}, getOptionsFromPrefix(props, 'form'));

    this.form = node;
    this.inputs = [];
    this.tabBar = this.form.querySelector('.tab-bar');

    // Disable the default form validation messages
    node.noValidate = true;

    // Listen for form submissions
    node.addEventListener('submit', this.onSubmit.bind(this));
  },

  /**
   * Handle form submission
   * @param {Object} e Event object
   * @return {void}
   */
  onSubmit(e) {
    this.inputs = toArray(this.form.querySelectorAll(this.inputElementTags.join(', ')));

    // Remove all validation errors
    this.removeValidationErrors();

    // By default we believe in the goodness of mankind
    let valid = true;

    // Loop through all input fields in the form
    this.inputs.forEach((input) => {
      // Get the validity of the input field, validity will be an object containing
      // different validation types like valueMissing
      const { validity } = input;

      // The validity object is not iterable :/ So we've defined all available
      // validation types on our object above and loop through them this way
      this.availableValidityChecks.forEach((validityCheck) => {
        // @type {Boolean} Get state of the current validation type
        const val = validity[validityCheck];

        // Check if the validation is hit (true means it's invalid for this type)
        if (val) {
          // Mark the whole form as invalid
          valid = false;

          this.addValidationErrorToTab(input);

          // Add error message on the input field
          this.addValidationError(input, validityCheck);
        }
      });
    });

    this.switchToErrorTab();

    // If the trust in the goodness of mankind is damaged
    // we prevent the form from submitting any data
    if (!valid) {
      e.preventDefault();
    }
  },

  /**
   * Go through all input elements and remove the validation classes and messages
   * @return {void}
   */
  removeValidationErrors() {
    this.inputs.forEach((input) => {
      input.className = input.className.replace(/is-invalid is-invalid-\w+/gi, '');

      const errMessageNode = input.nextElementSibling;

      if (errMessageNode && errMessageNode.className.indexOf('invalid-feedback') >= 0) {
        errMessageNode.parentNode.removeChild(errMessageNode);
      }
    });

    if (this.tabBar) {
      // Get all tabs that are marked as invalid
      const invalidTabs = toArray(this.tabBar.querySelectorAll('.is-invalid'));

      // Loop through all invalid tabs and remove the invalid class
      invalidTabs.forEach((tab) => {
        tab.className = tab.className.replace('is-invalid', '');
      });
    }
  },

  /**
   * Create a error message element
   * @param {String} translation Error message
   * @return {HTMLElement}
   */
  createErrorElement(translation) {
    const el = document.createElement('div');
    el.className = 'invalid-feedback';
    el.textContent = translation;
    el.setAttribute('aria-live', 'polite');
    return el;
  },

  /**
   * Add validation error to the input field
   * @param {HTMLInputElement} input
   * @param {String} validityKey Name of the validation that was triggered
   * @return {void}
   */
  addValidationError(input, validityKey) {
    // Add error classes to the input field
    input.className += ` is-invalid is-invalid-${validityKey}`;

    // Get the translation for this error from our global language variable
    const translation = LANG[validityKey];

    // Insert a error message after this input field
    insertAfter(this.createErrorElement(translation), input);
  },

  /**
   * Add validation errors thrown by an input field to the parent tab
   * @param {Node} input
   * @return {void}
   */
  addValidationErrorToTab(input) {
    // Get the parent panel element
    const panel = getClosest(input, '.tab-pane');

    if (!panel) {
      return;
    }

    // Get the tab element by ID
    const panelID = panel.id;
    const panelTab = this.tabBar.querySelector(`[href="#${panelID}"]`);

    // Add invalid class to tab for styling
    if (panelTab) {
      panelTab.className += ' is-invalid';
    }
  },

  /**
   * Switch to the tab with the error
   * @return {void}
   */
  switchToErrorTab() {
    if (!this.tabBar) {
      return;
    }

    let errorTabs = this.tabBar.querySelectorAll('.is-invalid');
    errorTabs = Array.prototype.slice.call(errorTabs);

    if (errorTabs.length) {
      const first = errorTabs[0];

      $(first).tab('show');
    }
  },
};

export default ManagedForm;
